/*
  Rotas da Wiki (docs/techspec-wiki.md §05; v2 em docs/techspec/techspec-wiki-v2.md §02). Sem biblioteca: o caminho
  é lido à mão, como o App.tsx faz com /convite e /alpha. O nginx já manda qualquer caminho para o index.html.
*/

export const RAIZ = '/wiki';

export const SISTEMAS = ['cacadas', 'coleta', 'fadiga', 'oficios', 'guarda-roupa', 'eventos', 'pvp', 'vip'] as const;
export type SistemaId = typeof SISTEMAS[number];

/** As categorias do database de itens (§02). */
export const CATEGORIAS = ['armas', 'armaduras', 'escudos', 'ferramentas', 'materiais', 'pocoes', 'municao', 'montarias', 'tintas', 'outros'] as const;
export type Categoria = typeof CATEGORIAS[number];

export type Rota =
  | { pag: 'inicio' }
  | { pag: 'sistemas' }
  | { pag: 'sistema'; id: SistemaId; sub: string }
  | { pag: 'classes' }
  | { pag: 'skills' }
  | { pag: 'magias'; circulo: number }
  | { pag: 'magia'; id: string }
  | { pag: 'itens'; cat: Categoria | '' }
  | { pag: 'item'; id: string }
  | { pag: 'monstros' }
  | { pag: 'monstro'; id: string }
  | { pag: 'sumiu' };

const ID = /^[a-z0-9-]{1,48}$/;
/** Item aceita a variante de material: `bow@oak` (techspec-materiais). */
const ID_ITEM = /^[a-z0-9-]{1,48}(@[a-z0-9-]{1,24})?$/;

/** Os grupos da v1 (`/wiki/itens/grupo/weapon`) caem na categoria da v2. */
const GRUPO_V1: Readonly<Record<string, Categoria>> = {
  weapon: 'armas', armor: 'armaduras', shield: 'escudos', ingot: 'materiais', ore: 'materiais', potion: 'pocoes',
  ammo: 'municao', mount: 'montarias', misc: 'outros', cloth: 'outros'
};

export function ehWiki(caminho: string): boolean {
  return caminho === RAIZ || caminho.startsWith(RAIZ + '/');
}

const ehSistema = (s: string | undefined): s is SistemaId => !!s && (SISTEMAS as readonly string[]).includes(s);
const ehCategoria = (s: string | undefined): s is Categoria => !!s && (CATEGORIAS as readonly string[]).includes(s);

export function lerRota(caminho: string): Rota {
  const partes = caminho.replace(/\/+$/, '').slice(RAIZ.length).split('/').filter(Boolean);
  const [a, b, c, d] = partes;
  if (!a) return { pag: 'inicio' };
  if (a === 'sistemas' && !b) return { pag: 'sistemas' };
  if (a === 'sistemas' && ehSistema(b) && !d) {
    if (!c) return { pag: 'sistema', id: b, sub: '' };
    if (b === 'cacadas' && ID.test(c)) return { pag: 'sistema', id: b, sub: c };
    return { pag: 'sumiu' };
  }
  // A v1 tinha Ofícios e Mundo como páginas: viraram sistemas.
  if (a === 'oficios' && !b) return { pag: 'sistema', id: 'oficios', sub: '' };
  if (a === 'mundo' && !b) return { pag: 'sistema', id: 'cacadas', sub: '' };
  if (a === 'classes' && !b) return { pag: 'classes' };
  if (a === 'skills' && !b) return { pag: 'skills' };
  if (a === 'magias' && !b) return { pag: 'magias', circulo: 0 };
  if (a === 'magias' && b && /^[1-8]$/.test(b) && !c) return { pag: 'magias', circulo: Number(b) };
  if (a === 'magia' && b && ID.test(b) && !c) return { pag: 'magia', id: b };
  if (a === 'itens' && !b) return { pag: 'itens', cat: '' };
  if (a === 'itens' && b === 'cat' && ehCategoria(c) && !d) return { pag: 'itens', cat: c };
  if (a === 'itens' && b === 'grupo' && c && GRUPO_V1[c] && !d) return { pag: 'itens', cat: GRUPO_V1[c] };
  if (a === 'itens' && b && ID_ITEM.test(b) && !c) return { pag: 'item', id: b };
  if (a === 'monstros' && !b) return { pag: 'monstros' };
  if (a === 'monstros' && b && ID.test(b) && !c) return { pag: 'monstro', id: b };
  return { pag: 'sumiu' };
}

export const caminho = {
  inicio: () => RAIZ,
  sistemas: () => `${RAIZ}/sistemas`,
  sistema: (id: SistemaId, sub = '') => (sub ? `${RAIZ}/sistemas/${id}/${sub}` : `${RAIZ}/sistemas/${id}`),
  classes: () => `${RAIZ}/classes`,
  /** A página de skills; com `id`, a âncora da linha da skill (`#anatomy`). */
  skills: (id = '') => (id ? `${RAIZ}/skills#${id}` : `${RAIZ}/skills`),
  magias: (circulo = 0) => (circulo ? `${RAIZ}/magias/${circulo}` : `${RAIZ}/magias`),
  magia: (id: string) => `${RAIZ}/magia/${id}`,
  itens: (cat: Categoria | '' = '') => (cat ? `${RAIZ}/itens/cat/${cat}` : `${RAIZ}/itens`),
  item: (id: string) => `${RAIZ}/itens/${id}`,
  monstros: () => `${RAIZ}/monstros`,
  monstro: (id: string) => `${RAIZ}/monstros/${id}`
};

/** Seção da barra do topo que fica acesa nesta rota. */
export function secaoDe(r: Rota): string {
  switch (r.pag) {
    case 'sistemas': case 'sistema': return 'sistemas';
    case 'magias': case 'magia': return 'magias';
    case 'itens': case 'item': return 'itens';
    case 'monstros': case 'monstro': return 'monstros';
    case 'classes': return 'classes';
    case 'skills': return 'skills';
    default: return '';
  }
}
